import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { API_BASE_URL } from '../config';
import './NiftyRealty.css';
import RefreshButton from './RefreshButton';
import WatchlistStar from './WatchlistStar';

// Timeframe configurations
const validTimeframes = {
  "5minute": { ma_length: 20, atr_period: 14, lookback_days: 5 },
  "15minute": { ma_length: 20, atr_period: 14, lookback_days: 10 },
  "30minute": { ma_length: 20, atr_period: 14, lookback_days: 15 },
  "hour": { ma_length: 20, atr_period: 14, lookback_days: 20 },
  "day": { ma_length: 20, atr_period: 14, lookback_days: 60 },
  "week": { ma_length: 20, atr_period: 14, lookback_days: 180 }
};

const shortTermTimeframes = ['5minute', '15minute', '30minute'];
const longTermTimeframes = ['hour', 'day', 'week'];

const NiftyRealty = ({ onBack }) => {
  const [universe, setUniverse] = useState('ALL'); // ALL or FO
  const [category, setCategory] = useState('10 DAY BREAKOUT');
  const [timeframe, setTimeframe] = useState('day');
  const [foSymbols, setFoSymbols] = useState([]);
  const [allSymbols, setAllSymbols] = useState([]);
  const [stocks, setStocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingSymbols, setLoadingSymbols] = useState(true);
  const [error, setError] = useState(null);


  // Attempt to load symbol universes dynamically; fall back if unavailable
  useEffect(() => {
    let cancelled = false;

    const loadUniverses = async () => {
      setLoadingSymbols(true);
      setError(null);
      try {
        // Fetch FO stocks from the backend
        const foRes = await fetch(`${API_BASE_URL}/fo-stocks`);
        let foList = [];
        if (foRes.ok) {
          const foJson = await foRes.json();
          foList = Array.isArray(foJson) ? foJson : [];
        } else {
          throw new Error('Failed to fetch F&O stocks');
        }

        // For  , we'll use the same list as FO stocks for now
        // If you have a separate endpoint for  , replace the URL accordingly
        const allList = [...foList];

        if (!cancelled) {
          setFoSymbols(foList);
          setAllSymbols(allList);
        }
      } catch (e) {
        console.error('Error loading stock symbols:', e);
        if (!cancelled) {
          setError('Failed to load stock symbols. Please try again later.');
        }
      } finally {
        if (!cancelled) setLoadingSymbols(false);
      }
    };

    loadUniverses();
    return () => {
      cancelled = true;
    };
  }, []);

  const selectedSymbols = useMemo(() => {
    const base = universe === 'FO' ? foSymbols : allSymbols;
    // To manage server load, sample a subset; adjust as needed or paginate
    return base.slice(0, 50);
  }, [universe, foSymbols, allSymbols]);

  const fetchData = useCallback(async () => {
    if (loadingSymbols) return;
      try {
        console.log('=== FETCH DATA CALLED ===');
        console.log('Current timeframe in fetchData:', timeframe);
        setLoading(true);
        setStocks([]);
        console.log('Cleared stocks array');
        console.log('Fetching data for category:', category, 'timeframe:', timeframe);
        
        if (category === '10 DAY BREAKOUT' || category === '50 DAY BREAKOUT') {
          // Convert selectedSymbols to the format expected by the backend
          const symbols = selectedSymbols.map(symbol => ({
            symbol: symbol,
            exchange: 'NSE'  // Assuming NSE as default exchange
          }));
          
          const response = await fetch(`${API_BASE_URL}/breakout-tenor-fifty`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              category: category === '10 DAY BREAKOUT' ? '10 DAY' : '50 DAY',
              symbols: symbols,
            }),
          });

          if (!response.ok) {
            throw new Error(`HTTP error! status : ${response.status}`);
          }

          const result = await response.json();
          // Extract data array from the response object
          const stocks = result.data && Array.isArray(result.data) ? result.data : [];
          setStocks(stocks);
        } else if (category === 'LONG TERM CHANNEL BREAKOUT' || category === 'SHORT TERM CHANNEL BREAKOUT') {
          console.log('=== CHANNEL BREAKOUT BRANCH ===');
          console.log('Current timeframe value:', timeframe);
          const timeframeConfig = validTimeframes[timeframe];
          if (!timeframeConfig) {
            throw new Error(`Invalid timeframe: ${timeframe}`);
          }
          console.log('Timeframe config found:', timeframeConfig);
          const requestPayload = {
            timeframes: [timeframe],
            ma_length: timeframeConfig.ma_length,
            atr_period: timeframeConfig.atr_period,
            lookback_days: timeframeConfig.lookback_days
          };
          
          console.log('Sending request to backend:', requestPayload);
          console.log('URL:', `${API_BASE_URL}/channel-breakout?t=${Date.now()}`);
          
          const response = await fetch(`${API_BASE_URL}/channel-breakout?t=${Date.now()}`, {
            method: 'POST',
            headers: { 
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(requestPayload),
            cache: 'no-store'
          });
          
          console.log('Response status:', response.status);
          
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          
          const result = await response.json();
          console.log('Response data:', result);
          console.log('Response timeframe:', result.timeframes);
          console.log('Data array length:', result.data ? result.data.length : 0);
          console.log('First item sample:', result.data && result.data[0]);
          console.log('First 3 symbols:', result.data ? result.data.slice(0, 3).map(d => d.symbol) : []);
          // Check if we got a successful response with data array
          if (result.status === 'success' && Array.isArray(result.data)) {
            // Use the data directly from backend without remapping
            console.log('Setting stocks with', result.data.length, 'items');
            console.log('First item symbol:', result.data[0]?.symbol);
            setStocks(result.data);
            console.log('State updated with', result.data.length, 'items');
          } else {
            console.log('No valid data received, setting empty array');
            setStocks([]);
          }
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        setError('Failed to load data. Please try again later.');
      } finally {
        setLoading(false);
      }
  }, [category, selectedSymbols, loadingSymbols, timeframe]);

  useEffect(() => {
    if (category === 'SHORT TERM CHANNEL BREAKOUT' && !shortTermTimeframes.includes(timeframe)) {
      setTimeframe('5minute');
    }
    if (category === 'LONG TERM CHANNEL BREAKOUT' && !longTermTimeframes.includes(timeframe)) {
      setTimeframe('day');
    }
  }, [category, timeframe]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    console.log('Timeframe changed to:', timeframe);
  }, [timeframe]);

  const handleTimeframeChange = (newTimeframe) => {
    console.log('Button clicked - changing timeframe from', timeframe, 'to', newTimeframe);
    if (newTimeframe === timeframe) {
      console.log('Same timeframe clicked, forcing refresh');
      setStocks([]);
    }
    setTimeframe(newTimeframe);
    console.log('Timeframe state will be updated to:', newTimeframe);
  };

  return (
    <div className="nifty-realty-page">
      {/* Header */}
      <header className="nifty-header">
        <div className="nifty-header-container">
          <div className="logo">
            <img src="images/Final Font Stocks4U1.png" className="logo-text" alt="Stocks4U" />
          </div>
          
          <div className="nifty-user">
            <RefreshButton 
              onRefresh={fetchData} 
              isLoading={loading}
              size="small"
              style={{ marginRight: '10px' }}
            />
          </div>
        </div>
      </header>

      {/* Main Section */}
      <main className="nifty-main">
        <div className="nifty-container">
          <div className="nifty-title">
            <div className="s4u-title-row">
              <div className="s4u-page-heading">
                <button className="nifty-back-btn s4u-back-btn" onClick={onBack}>← Back to Dashboard</button>
                <h1>Swing Zone <span className="info-icon">ℹ️</span></h1>
              </div>
            </div>
            <p className="warning">In Swing zone data will be shown for 10 days</p>

            <h2 className="category-heading">Categories</h2>
            <div className="breakout-buttons">
              <button
                className={category === '10 DAY BREAKOUT' ? 'breakout-btn active' : 'breakout-btn'}
                onClick={() => setCategory('10 DAY BREAKOUT')}
              >
                10 DAY BREAKOUT
                <p className="subtext">How to use ?</p>
              </button>
              <button
                className={category === '50 DAY BREAKOUT' ? 'breakout-btn active' : 'breakout-btn'}
                onClick={() => setCategory('50 DAY BREAKOUT')}
              >
                50 DAY BREAKOUT
                <p className="subtext">How to use ?</p>
              </button>
              <button
                className={category === 'LONG TERM CHANNEL BREAKOUT' ? 'breakout-btn active' : 'breakout-btn'}
                onClick={() => {
                  setCategory('LONG TERM CHANNEL BREAKOUT');
                  if (!longTermTimeframes.includes(timeframe)) setTimeframe('day');
                }}
              >
                LONG TERM CHANNEL BREAKOUT
                <p className="subtext">How to use ?</p>
              </button>
              <button
                className={category === 'SHORT TERM CHANNEL BREAKOUT' ? 'breakout-btn active' : 'breakout-btn'}
                onClick={() => {
                  setCategory('SHORT TERM CHANNEL BREAKOUT');
                  if (!shortTermTimeframes.includes(timeframe)) setTimeframe('5minute');
                }}
              >
                SHORT TERM CHANNEL BREAKOUT
                <p className="subtext">How to use ?</p>
              </button>
            </div>

            {category === 'SHORT TERM CHANNEL BREAKOUT' && (
              <div className="timeframe-buttons">
                <button
                  className={timeframe === '5minute' ? 'timeframe-btn active' : 'timeframe-btn'}
                  onClick={() => handleTimeframeChange('5minute')}
                  disabled={loading}
                >
                  5 Minute
                </button>
                <button
                  className={timeframe === '15minute' ? 'timeframe-btn active' : 'timeframe-btn'}
                  onClick={() => handleTimeframeChange('15minute')}
                  disabled={loading}
                >
                  15 Minute
                </button>
                <button
                  className={timeframe === '30minute' ? 'timeframe-btn active' : 'timeframe-btn'}
                  onClick={() => handleTimeframeChange('30minute')}
                  disabled={loading}
                >
                  30 Minute
                </button>
              </div>
            )}

            {category === 'LONG TERM CHANNEL BREAKOUT' && (
              <div className="timeframe-buttons">
                <button
                  className={timeframe === 'hour' ? 'timeframe-btn active' : 'timeframe-btn'}
                  onClick={() => handleTimeframeChange('hour')}
                  disabled={loading}
                >
                  1 Hour
                </button>
                <button
                  className={timeframe === 'day' ? 'timeframe-btn active' : 'timeframe-btn'}
                  onClick={() => handleTimeframeChange('day')}
                  disabled={loading}
                >
                  1 Day
                </button>
                <button
                  className={timeframe === 'week' ? 'timeframe-btn active' : 'timeframe-btn'}
                  onClick={() => handleTimeframeChange('week')}
                  disabled={loading}
                >
                  1 Week
                </button>
              </div>
            )}
          </div>

          {loading ? (
            <div className="loading">⏳ Loading stock data...</div>
          ) : error ? (
            <div className="error-message">
              <p>{error}</p>
              <button onClick={() => window.location.reload()} className="retry-button">
                Retry
              </button>
            </div>
          ) : (
            <div className={`swing-zone-table ${(category === 'LONG TERM CHANNEL BREAKOUT' || category === 'SHORT TERM CHANNEL BREAKOUT') ? 'channel-breakout-table' : ''}`} key={`${category}-${timeframe}-${stocks.length}`}>
              <table>
                {(category === '10 DAY BREAKOUT' || category === '50 DAY BREAKOUT') ? (
                  <colgroup>
                    <col style={{ width: '5%' }} />
                    <col style={{ width: '20%' }} />
                    <col style={{ width: '20%' }} />
                    <col style={{ width: '18%' }} />
                    <col style={{ width: '18%' }} />
                    <col style={{ width: '19%' }} />
                  </colgroup>
                ) : (
                  <colgroup>
                    <col style={{ width: '25%' }} />
                    <col style={{ width: '25%' }} />
                    <col style={{ width: '20%' }} />
                    <col style={{ width: '20%' }} />
                    <col style={{ width: '10%' }} />
                  </colgroup>
                )}
                <thead>
                  <tr>
                    {(category === '10 DAY BREAKOUT' || category === '50 DAY BREAKOUT') && <th>⭐</th>}
                    <th>Name</th>
                    <th>Date &amp; Time</th>
                    <th>Percentage</th>
                    {(category === '10 DAY BREAKOUT' || category === '50 DAY BREAKOUT') && <th>Current Percentage</th>}
                    {(category === '10 DAY BREAKOUT' || category === '50 DAY BREAKOUT') && <th>SIGNALS</th>}
                    {(category === 'LONG TERM CHANNEL BREAKOUT' || category === 'SHORT TERM CHANNEL BREAKOUT') && <th>Close</th>}
                    {(category === 'LONG TERM CHANNEL BREAKOUT' || category === 'SHORT TERM CHANNEL BREAKOUT') && <th>Volume</th>}
                  </tr>
                </thead>
                <tbody>
                  {stocks.length > 0 ? (
                    stocks.map((stock, index) => (
                      <tr key={index}>
                        {(category === '10 DAY BREAKOUT' || category === '50 DAY BREAKOUT') && (
                          <td style={{ textAlign: 'center' }}>
                            <WatchlistStar
                              stock={{
                                symbol: stock.symbol,
                                name: stock.symbol,
                                breakout_time: stock.breakout_time,
                                breakout_pct: stock.breakout_pct,
                                after_bo: stock.after_bo,
                                chart: stock.chart
                              }}
                              size="18px"
                            />
                          </td>
                        )}
                        <td style={{ textAlign: 'left' }}>
                          {(category === 'LONG TERM CHANNEL BREAKOUT' || category === 'SHORT TERM CHANNEL BREAKOUT') && (
                            <WatchlistStar
                              stock={{
                                symbol: stock.symbol,
                                name: stock.symbol,
                                date: stock.breakout_time,
                                percentage: stock.breakout_pct,
                                close: stock.latest_close
                              }}
                              size="16px"
                            />
                          )}
                          {stock.symbol || 'N/A'}
                        </td>
                        <td style={{ textAlign: 'center' }}>{stock.breakout_time || 'N/A'}</td>
                        <td style={{ textAlign: 'center' }}>
                          <span className={stock.breakout_pct && !String(stock.breakout_pct).includes('-') ? 'positive' : 'negative'}>
                            {stock.breakout_pct !== undefined && stock.breakout_pct !== null && stock.breakout_pct !== ''
                              ? `${String(stock.breakout_pct).includes('-') ? '' : '+'}${stock.breakout_pct}%`
                              : '—'}
                          </span>
                        </td>
                        {(category === '10 DAY BREAKOUT' || category === '50 DAY BREAKOUT') && (
                          <td style={{ textAlign: 'center' }}>
                            <span className={stock.after_bo && !String(stock.after_bo).includes('-') ? 'positive' : 'negative'}>
                              {stock.after_bo !== undefined && stock.after_bo !== null && stock.after_bo !== ''
                                ? `${String(stock.after_bo).includes('-') ? '' : '+'}${Number(stock.after_bo).toFixed(2)}%`
                                : '—'}
                            </span>
                          </td>
                        )}
                        {(category === '10 DAY BREAKOUT' || category === '50 DAY BREAKOUT') && (
                          <td style={{ textAlign: 'center' }}>
                            {stock.chart === 'up' ? (
                              <span className="signal-up" style={{ color: '#10b981', fontSize: '1.2rem' }}>↑</span>
                            ) : stock.chart === 'down' ? (
                              <span className="signal-down" style={{ color: '#ef4444', fontSize: '1.2rem' }}>↓</span>
                            ) : (
                              '—'
                            )}
                          </td>
                        )}
                        {(category === 'LONG TERM CHANNEL BREAKOUT' || category === 'SHORT TERM CHANNEL BREAKOUT') && (
                          <td style={{ textAlign: 'center' }}>₹{
                            stock.latest_close
                              ? Number(stock.latest_close).toLocaleString('en-IN', {maximumFractionDigits: 2})
                              : 'N/A'
                          }</td>
                        )}
                        {(category === 'LONG TERM CHANNEL BREAKOUT' || category === 'SHORT TERM CHANNEL BREAKOUT') && (
                          <td style={{ textAlign: 'center' }}>{
                            stock.volume
                              ? Number(stock.volume).toLocaleString('en-IN')
                              : 'N/A'
                          }</td>
                        )}
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={(category === '10 DAY BREAKOUT' || category === '50 DAY BREAKOUT') ? 6 : 5} style={{ textAlign: 'center' }}>
                        No data available for {category.toLowerCase()}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default NiftyRealty;
